import React, { useCallback } from 'react';
import { BlurView } from 'expo-blur';
import { useIntl } from 'react-intl';
import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native';

import { GlassView, Icon, theme } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import { useIsLiquidGlassEnabled } from '../../../../../../../../../../hooks';
import { locationResultsListsV2ExpandButtonMessages as messages } from './LocationResultsListsV2ExpandButton.messages';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultsListsV2ExpandButton = (
  props: LocationResultsListsV2ExpandButtonProps,
) => {
  const { onPress } = props;

  const { formatMessage } = useIntl();
  const { track } = useTelemetry();

  const isLiquidGlassEnabled = useIsLiquidGlassEnabled();

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleOnPress = useCallback(() => {
    onPress?.();

    track('location.listview.tapped');
  }, [onPress, track]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const backgroundStyles = [
    styles.background,
    isLiquidGlassEnabled ? undefined : styles.backgroundUpdate,
  ];
  return (
    <TouchableOpacity
      activeOpacity={ACTIVE_OPACITY}
      accessibilityRole="button"
      onPress={handleOnPress}
      aria-label={formatMessage(messages.expandButtonA11yLabel)}
      style={styles.touchable}
    >
      <View style={backgroundStyles} pointerEvents="none">
        {isLiquidGlassEnabled ? (
          <GlassView
            glassEffectStyle="regular"
            tintColor={GLASS_TINT_COLOR}
            style={StyleSheet.absoluteFill}
          />
        ) : (
          <BlurView
            intensity={LOCATION_CONTAINER_BLUR_INTENSITY}
            tint="light"
            style={StyleSheet.absoluteFill}
          />
        )}
      </View>

      <View style={styles.iconContainer} pointerEvents="none">
        <Icon name="IconList" width={ICON_SIZE} height={ICON_SIZE} color={theme.colors.DARK_KALE} />
      </View>
    </TouchableOpacity>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ACTIVE_OPACITY = 0.8;
const ICON_SIZE = 24;
const LOCATION_CONTAINER_BLUR_INTENSITY = 85;
const GLASS_TINT_COLOR = 'rgba(255, 255, 255, 0.3)';

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  touchable: {
    alignSelf: 'flex-end',
    padding: theme.spacing[2],
    borderRadius: ICON_SIZE,
    ...theme.elevations['4'],
  },
  background: {
    ...StyleSheet.absoluteFill,
    borderRadius: ICON_SIZE,
    overflow: 'hidden',
  },
  backgroundUpdate: {
    backgroundColor: Platform.select({
      android: 'rgba(250, 250, 246, 0.95)',
      default: 'rgba(250, 250, 246, 0.7)',
    }),
  },
  iconContainer: {
    zIndex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type LocationResultsListsV2ExpandButtonProps = {
  onPress: () => void;
};
